// apps/web/src/components/dashboard/CompleteDashboard.tsx
// Complete dashboard integrating all platform features

import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/apiClient';

export function CompleteDashboard() {
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalCreators: 0,
    totalCampaigns: 0,
    totalContent: 0,
    totalInvoices: 0,
    totalPayments: 0,
    totalDeliverables: 0,
    totalRights: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch all stats
        const [tenants, creators, campaigns, content, invoices, payments, deliverables, rights] = await Promise.all([
          apiClient.get('/api/v1/tenants'),
          apiClient.get('/api/v1/creators'),
          apiClient.get('/api/v1/campaigns'),
          apiClient.get('/api/v1/content'),
          apiClient.get('/api/v1/finance/invoices'),
          apiClient.get('/api/v1/finance/payments'),
          apiClient.get('/api/v1/deliverables'),
          apiClient.get('/api/v1/rights')
        ]);
        
        setStats({
          totalTenants: tenants.data.tenants?.length || 0,
          totalCreators: creators.data.creators?.length || 0,
          totalCampaigns: campaigns.data.campaigns?.length || 0,
          totalContent: content.data.content?.length || 0,
          totalInvoices: invoices.data.invoices?.length || 0,
          totalPayments: payments.data.payments?.length || 0,
          totalDeliverables: deliverables.data.deliverables?.length || 0,
          totalRights: rights.data.rights?.length || 0
        });
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="complete-dashboard">
      <h1>Platform Dashboard</h1>
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{stats.totalTenants}</h3>
          <p>Tenants</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalCreators}</h3>
          <p>Creators</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalCampaigns}</h3>
          <p>Campaigns</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalContent}</h3>
          <p>Content Assets</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalInvoices}</h3>
          <p>Invoices</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalPayments}</h3>
          <p>Payments</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalDeliverables}</h3>
          <p>Deliverables</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalRights}</h3>
          <p>Rights</p>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="section">
          <h2>Recent Activity</h2>
          <div className="activity-feed">
            <p>No recent activity to display</p>
          </div>
        </div>
        
        <div className="section">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <button className="btn btn-primary">Create Campaign</button>
            <button className="btn btn-primary">Add Creator</button>
            <button className="btn btn-primary">Upload Content</button>
            <button className="btn btn-primary">Create Invoice</button>
            <button className="btn btn-primary">Manage Rights</button>
            <button className="btn btn-primary">View Payments</button>
          </div>
        </div>
      </div>

      <div className="platform-features">
        <h2>Platform Features</h2>
        <div className="features-grid">
          <div className="feature-card">
            <h3>Content Management</h3>
            <p>Upload, organize, and manage all your creative assets</p>
            <button className="btn btn-outline">Manage Content</button>
          </div>
          <div className="feature-card">
            <h3>Creator Collaboration</h3>
            <p>Work with creators, manage deliverables, and track progress</p>
            <button className="btn btn-outline">Manage Creators</button>
          </div>
          <div className="feature-card">
            <h3>Financial Tracking</h3>
            <p>Manage invoices, payments, and financial reporting</p>
            <button className="btn btn-outline">View Finance</button>
          </div>
          <div className="feature-card">
            <h3>Rights Management</h3>
            <p>Control usage rights and licensing for your content</p>
            <button className="btn btn-outline">Manage Rights</button>
          </div>
        </div>
      </div>
    </div>
  );
}